import { useRef, useCallback, useEffect } from 'react';

const isDev =
  process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test';

// ─── Pure helpers (defined outside hook to avoid recreation) ─────────────────

function avg(arr) {
  if (arr.length === 0) return null;
  return arr.reduce((a, b) => a + b, 0) / arr.length;
}

function maxVal(arr) {
  if (arr.length === 0) return null;
  return Math.max(...arr);
}

function percentile(arr, p) {
  if (arr.length === 0) return null;
  const sorted = [...arr].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length * p)];
}

/** Lower is better (e.g., render time, latency) */
function indicator(val, good, warn) {
  if (val === null || val === undefined) return '⚪';
  if (val <= good) return '🟢';
  if (val <= warn) return '🟡';
  return '🔴';
}

/** Higher is better (e.g., FPS) */
function indicatorHigh(val, good, warn) {
  if (val === null || val === undefined) return '⚪';
  if (val >= good) return '🟢';
  if (val >= warn) return '🟡';
  return '🔴';
}

function fmt(val, decimals = 2) {
  return val !== null && val !== undefined ? Number(val).toFixed(decimals) : 'N/A';
}

function createEmptyMetrics() {
  return {
    // Render timing
    renderTimes: [],           // ms per renderAll() call
    renderAllCalls: 0,         // synchronous renderAll() call count
    requestRenderAllCalls: 0,  // async requestRenderAll() call count
    renderTimestamps: [],      // performance.now() timestamps of each renderAll()

    // Hit-detection
    hitDetectionTimes: [],     // ms per findTarget() call

    // Object:moving latency (caller-provided, event → render-complete)
    objectMovingLatencies: [], // ms

    // Mouse-move throughput
    mouseMoves: 0,
    mouseMoveTimes: [],        // ms between consecutive mouse:move events
    lastMouseMoveTime: 0,

    // Misc object / React tracking
    objectsCreated: 0,
    reactRenders: 0,

    // Test-scenario label for before/after comparisons
    scenario: 'default',
    startTime: performance.now(),
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Performance monitoring hook for Fabric.js canvas.
 *
 * Only active when NODE_ENV === 'development'. All methods are no-ops in
 * production so it is safe to leave calls in the source tree.
 *
 * Patches canvas.renderAll(), canvas.requestRenderAll() and canvas.findTarget()
 * to capture timing data with zero runtime cost in production.
 *
 * In development the monitor is also exposed as `window.perfMonitor` so it can
 * be accessed directly from the browser DevTools console:
 *
 *   perfMonitor.setScenario('S-Mittel');
 *   perfMonitor.printReport();
 *   const json = perfMonitor.exportJSON();   // copy for before/after comparison
 *   perfMonitor.reset();
 *
 * @param {import('fabric').Canvas|null} canvas - Fabric.js canvas instance
 * @param {string} label - Display label shown in console reports
 */
export default function useCanvasPerformanceMonitor(canvas, label = 'Canvas') {
  // Lazy-initialise once, never replaced (reset() writes into the same object)
  const metricsRef = useRef(null);
  if (metricsRef.current === null) {
    metricsRef.current = createEmptyMetrics();
  }

  // Count React re-renders (cheap; only written in dev). Intentional ref write
  // during render — the counter is never read for rendering output, only surfaced
  // in the dev console reports below.
  if (isDev) {
    // eslint-disable-next-line react-hooks/refs -- dev-only render counter; not used for render output
    metricsRef.current.reactRenders += 1;
  }

  // ── Patch canvas methods ────────────────────────────────────────────────────
  // Dev-only instrumentation: temporarily monkey-patches the Fabric canvas
  // methods to capture timing, restoring the originals in cleanup. The
  // immutability rule flags this intentional imperative patching of `canvas`.
  // eslint-disable-next-line react-hooks/immutability -- dev-only canvas instrumentation; originals restored in cleanup
  useEffect(() => {
    if (!canvas || !isDev) return;

    // renderAll() — synchronous, easy to time
    const origRenderAll = canvas.renderAll.bind(canvas);
    // eslint-disable-next-line react-hooks/immutability -- dev-only canvas instrumentation; original restored in cleanup
    canvas.renderAll = function (...args) {
      const start = performance.now();
      const result = origRenderAll(...args);
      const elapsed = performance.now() - start;
      const m = metricsRef.current;
      m.renderTimes.push(elapsed);
      m.renderAllCalls += 1;
      m.renderTimestamps.push(performance.now());
      return result;
    };

    // requestRenderAll() — async via rAF, only count calls (elapsed not measurable here)
    const origRequestRenderAll = canvas.requestRenderAll.bind(canvas);
    canvas.requestRenderAll = function (...args) {
      metricsRef.current.requestRenderAllCalls += 1;
      return origRequestRenderAll(...args);
    };

    // findTarget() — hit-detection kernel, called on every mouse:move
    const origFindTarget = canvas.findTarget?.bind(canvas);
    if (origFindTarget) {
      canvas.findTarget = function (...args) {
        const start = performance.now();
        const result = origFindTarget(...args);
        metricsRef.current.hitDetectionTimes.push(performance.now() - start);
        return result;
      };
    }

    return () => {
      canvas.renderAll = origRenderAll;
      canvas.requestRenderAll = origRequestRenderAll;
      if (origFindTarget) canvas.findTarget = origFindTarget;
    };
  }, [canvas]);

  // ── Tracking helpers ────────────────────────────────────────────────────────

  /** Call when a new Fabric object is created (optional, for statistics). */
  const trackObjectCreate = useCallback((count = 1) => {
    if (isDev) metricsRef.current.objectsCreated += count;
  }, []);

  /** Call inside the canvas mouse:move handler to track throughput. */
  const trackMouseMove = useCallback(() => {
    if (!isDev) return;
    const now = performance.now();
    const m = metricsRef.current;
    m.mouseMoves += 1;
    if (m.lastMouseMoveTime > 0) {
      m.mouseMoveTimes.push(now - m.lastMouseMoveTime);
    }
    m.lastMouseMoveTime = now;
  }, []);

  /**
   * Record object:moving latency measured by the caller
   * (time from object:moving event receipt to renderAll completion).
   * @param {number} latencyMs
   */
  const trackObjectMoving = useCallback((latencyMs) => {
    if (isDev) metricsRef.current.objectMovingLatencies.push(latencyMs);
  }, []);

  /** Label the current measurement window for report comparisons. */
  const setScenario = useCallback((scenarioLabel) => {
    if (isDev) metricsRef.current.scenario = scenarioLabel;
  }, []);

  // ── Report helpers ──────────────────────────────────────────────────────────

  /**
   * Print a colour-coded performance report to the browser console.
   * Indicators: 🟢 good  🟡 marginal  🔴 poor  ⚪ neutral/N/A
   */
  const printReport = useCallback(() => {
    if (!isDev) return;
    const m = metricsRef.current;

    const avgRender = avg(m.renderTimes);
    const maxRender = maxVal(m.renderTimes);
    const p95Render = percentile(m.renderTimes, 0.95);
    const avgHit = avg(m.hitDetectionTimes);
    const maxHit = maxVal(m.hitDetectionTimes);
    const avgMoving = avg(m.objectMovingLatencies);
    const mouseFps =
      m.mouseMoveTimes.length > 0 ? 1000 / avg(m.mouseMoveTimes) : null;
    const elapsed = ((performance.now() - m.startTime) / 1000).toFixed(1);

    // Renders/sec: count renderAll timestamps within the last 1 second
    const now = performance.now();
    const recentRenders = m.renderTimestamps.filter(t => now - t < 1000).length;

    let objects = { total: 0, interactive: 0, nonInteractive: 0 };
    if (canvas) {
      const all = canvas.getObjects();
      const interactive = all.filter(o => o.evented !== false && o.selectable !== false);
      objects = {
        total: all.length,
        interactive: interactive.length,
        nonInteractive: all.length - interactive.length,
      };
    }

    console.group(
      `%c[PerfMonitor: ${label}] | Scenario: ${m.scenario} | Elapsed: ${elapsed}s`,
      'font-weight:bold;color:#10B981'
    );
    console.table({
      [`${indicator(avgRender, 8, 16)} Avg Render Time (ms)`]: fmt(avgRender),
      [`${indicator(maxRender, 16, 33)} Max Render Time (ms)`]: fmt(maxRender),
      [`${indicator(p95Render, 12, 25)} p95 Render Time (ms)`]: fmt(p95Render),
      [`⚪ renderAll() calls (sync)`]: m.renderAllCalls,
      [`⚪ requestRenderAll() calls`]: m.requestRenderAllCalls,
      [`${indicator(recentRenders, 5, 30)} Renders/sec (last 1s)`]: recentRenders,
      [`${indicator(avgHit, 2, 5)} Avg Hit-Detection (ms)`]: fmt(avgHit),
      [`${indicator(maxHit, 5, 10)} Max Hit-Detection (ms)`]: fmt(maxHit),
      [`${indicator(avgMoving, 16, 33)} Avg Object:Moving Latency (ms)`]: fmt(avgMoving),
      [`${indicatorHigh(mouseFps, 55, 30)} Mouse Move FPS`]: fmt(mouseFps, 0),
      [`⚪ Total Canvas Objects`]: objects.total,
      [`⚪ Interactive Objects`]: objects.interactive,
      [`⚪ Non-Interactive Objects`]: objects.nonInteractive,
      [`⚪ Objects Created`]: m.objectsCreated,
      [`⚪ React Re-Renders`]: m.reactRenders,
    });
    console.groupEnd();
  }, [canvas, label]);

  /**
   * Export all metrics as a JSON-serialisable object.
   * Also logs the JSON to the console for easy copy-paste comparison.
   * @returns {object|null} Report object, or null in production.
   */
  const exportJSON = useCallback(() => {
    if (!isDev) return null;
    const m = metricsRef.current;

    let objects = { total: 0, interactive: 0, nonInteractive: 0 };
    if (canvas) {
      const all = canvas.getObjects();
      const interactive = all.filter(o => o.evented !== false && o.selectable !== false);
      objects = {
        total: all.length,
        interactive: interactive.length,
        nonInteractive: all.length - interactive.length,
      };
    }

    const report = {
      label,
      scenario: m.scenario,
      timestamp: new Date().toISOString(),
      elapsedSeconds: Number(((performance.now() - m.startTime) / 1000).toFixed(2)),
      render: {
        avgMs: avg(m.renderTimes),
        maxMs: maxVal(m.renderTimes),
        p95Ms: percentile(m.renderTimes, 0.95),
        renderAllCalls: m.renderAllCalls,
        requestRenderAllCalls: m.requestRenderAllCalls,
        totalRenders: m.renderTimes.length,
      },
      hitDetection: {
        avgMs: avg(m.hitDetectionTimes),
        maxMs: maxVal(m.hitDetectionTimes),
        p95Ms: percentile(m.hitDetectionTimes, 0.95),
        totalCalls: m.hitDetectionTimes.length,
      },
      objectMoving: {
        avgLatencyMs: avg(m.objectMovingLatencies),
        samples: m.objectMovingLatencies.length,
      },
      mouseMove: {
        totalEvents: m.mouseMoves,
        avgFps: m.mouseMoveTimes.length > 0 ? 1000 / avg(m.mouseMoveTimes) : null,
      },
      objects: {
        total: objects.total,
        interactive: objects.interactive,
        nonInteractive: objects.nonInteractive,
        created: m.objectsCreated,
      },
      react: {
        renders: m.reactRenders,
      },
    };

    console.log('[PerfMonitor] JSON export (copy below):');
    console.log(JSON.stringify(report, null, 2));
    return report;
  }, [canvas, label]);

  /** Reset all counters and timers (useful between test scenarios). */
  const reset = useCallback(() => {
    if (isDev) metricsRef.current = createEmptyMetrics();
  }, []);

  return {
    trackObjectCreate,
    trackMouseMove,
    trackObjectMoving,
    setScenario,
    printReport,
    exportJSON,
    reset,
  };
}
